export interface ImageAnalysis {
  brightness: number;
  contrast: number;
  sharpness: number;
  histogram: number[];
  detectedAngle: number;
  overallQuality: 'poor' | 'fair' | 'good' | 'excellent';
  issues: string[];
  recommendations: ImageAdjustments;
}

export interface ImageAdjustments {
  brightness: number;
  contrast: number;
  sharpness: number;
  rotation: number;
  saturation: number;
  confidence: number; // 0-100，建議的信心度
}

export class ImageAnalyzer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  constructor() {
    this.canvas = document.createElement('canvas');
    this.ctx = this.canvas.getContext('2d')!;
  }

  async analyzeImage(file: File): Promise<ImageAnalysis> {
    const image = await this.loadImage(file);
    const imageData = this.getImageData(image);
    
    const brightness = this.calculateBrightness(imageData);
    const contrast = this.calculateContrast(imageData);
    const sharpness = this.calculateSharpness(imageData);
    const histogram = this.calculateHistogram(imageData);
    const detectedAngle = this.detectSkewAngle(imageData);
    
    const analysis = this.evaluateQuality(brightness, contrast, sharpness, histogram, detectedAngle);
    const recommendations = this.generateRecommendations(brightness, contrast, sharpness, histogram, detectedAngle);

    return {
      brightness,
      contrast,
      sharpness,
      histogram,
      detectedAngle,
      overallQuality: analysis.quality,
      issues: analysis.issues,
      recommendations
    };
  }

  private async loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };
      
      img.src = url;
    });
  }

  private getImageData(image: HTMLImageElement): ImageData {
    // 限制分析圖片大小以提升效能
    const maxSize = 800;
    let width = image.width;
    let height = image.height;
    
    if (width > maxSize || height > maxSize) {
      const scale = maxSize / Math.max(width, height);
      width *= scale;
      height *= scale;
    }
    
    this.canvas.width = width;
    this.canvas.height = height;
    this.ctx.drawImage(image, 0, 0, width, height);
    
    return this.ctx.getImageData(0, 0, width, height);
  }

  private calculateBrightness(imageData: ImageData): number {
    const data = imageData.data;
    let totalBrightness = 0;
    
    for (let i = 0; i < data.length; i += 4) {
      const brightness = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      totalBrightness += brightness;
    }
    
    return totalBrightness / (data.length / 4);
  }

  private calculateContrast(imageData: ImageData): number {
    const data = imageData.data;
    const grayscale: number[] = [];
    
    for (let i = 0; i < data.length; i += 4) {
      const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      grayscale.push(gray);
    }
    
    const mean = grayscale.reduce((a, b) => a + b, 0) / grayscale.length;
    const variance = grayscale.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / grayscale.length;
    
    return Math.sqrt(variance);
  }

  private calculateSharpness(imageData: ImageData): number {
    const data = imageData.data;
    const width = imageData.width;
    const height = imageData.height;
    
    let totalGradient = 0;
    let pixelCount = 0;
    
    // 使用 Sobel 算子檢測邊緣
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = (y * width + x) * 4;
        
        // 取得灰階值
        // const center = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
        
        // 計算水平和垂直梯度
        const left = 0.299 * data[idx - 4] + 0.587 * data[idx - 3] + 0.114 * data[idx - 2];
        const right = 0.299 * data[idx + 4] + 0.587 * data[idx + 5] + 0.114 * data[idx + 6];
        const top = 0.299 * data[idx - width * 4] + 0.587 * data[idx - width * 4 + 1] + 0.114 * data[idx - width * 4 + 2];
        const bottom = 0.299 * data[idx + width * 4] + 0.587 * data[idx + width * 4 + 1] + 0.114 * data[idx + width * 4 + 2];
        
        const gx = right - left;
        const gy = bottom - top;
        const gradient = Math.sqrt(gx * gx + gy * gy);
        
        totalGradient += gradient;
        pixelCount++;
      }
    }
    
    return totalGradient / pixelCount;
  }

  private calculateHistogram(imageData: ImageData): number[] {
    const data = imageData.data;
    const histogram = new Array(256).fill(0);
    
    for (let i = 0; i < data.length; i += 4) {
      const gray = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
      histogram[gray]++;
    }
    
    return histogram;
  }

  private detectSkewAngle(imageData: ImageData): number {
    // 使用邊緣檢測 + Hough Transform 檢測傾斜角度
    const edgeData = this.detectEdges(imageData);
    const lines = this.houghTransform(edgeData);
    const dominantAngle = this.findDominantAngle(lines);
    
    // 將角度限制在 -45 到 +45 度範圍內
    return Math.max(-45, Math.min(45, dominantAngle));
  }

  private detectEdges(imageData: ImageData): ImageData {
    const data = imageData.data;
    const width = imageData.width;
    const height = imageData.height;
    const edgeData = new Uint8ClampedArray(data.length);
    
    // Sobel 邊緣檢測
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = (y * width + x) * 4;
        
        // 取得灰階值
        const getGray = (offset: number) => {
          const i = idx + offset;
          return 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        };
        
        // Sobel X 方向
        const gx = getGray(4) + 2 * getGray(width * 4 + 4) + getGray(width * 8 + 4) -
                   getGray(-4) - 2 * getGray(width * 4 - 4) - getGray(width * 8 - 4);
        
        // Sobel Y 方向
        const gy = getGray(-width * 4) + 2 * getGray(-width * 4 + 4) + getGray(-width * 4 + 8) -
                   getGray(width * 4) - 2 * getGray(width * 4 + 4) - getGray(width * 4 + 8);
        
        const magnitude = Math.sqrt(gx * gx + gy * gy);
        const threshold = 50; // 邊緣檢測閾值
        
        edgeData[idx] = edgeData[idx + 1] = edgeData[idx + 2] = magnitude > threshold ? 255 : 0;
        edgeData[idx + 3] = 255;
      }
    }
    
    return new ImageData(edgeData, width, height);
  }

  private houghTransform(edgeData: ImageData): Array<{angle: number, strength: number}> {
    const data = edgeData.data;
    const width = edgeData.width;
    const height = edgeData.height;
    const angleStep = 1; // 每1度一個步長
    const angles: Array<{angle: number, strength: number}> = [];
    
    // 只檢測 -45 到 +45 度範圍
    for (let angle = -45; angle <= 45; angle += angleStep) {
      let strength = 0;
      const rad = (angle * Math.PI) / 180;
      const cosTheta = Math.cos(rad);
      const sinTheta = Math.sin(rad);
      
      // 掃描整個圖片
      for (let y = 0; y < height; y += 2) { // 跳過一些像素以提升效能
        for (let x = 0; x < width; x += 2) {
          const idx = (y * width + x) * 4;
          
          if (data[idx] > 0) { // 如果是邊緣點
            // 檢查這個點是否在指定角度的直線上
            // const rho = x * cosTheta + y * sinTheta;
            
            // 檢查附近是否有更多邊緣點形成直線
            let lineStrength = 0;
            for (let i = -20; i <= 20; i += 4) {
              const checkX = Math.round(x + i * (-sinTheta));
              const checkY = Math.round(y + i * cosTheta);
              
              if (checkX >= 0 && checkX < width && checkY >= 0 && checkY < height) {
                const checkIdx = (checkY * width + checkX) * 4;
                if (data[checkIdx] > 0) {
                  lineStrength++;
                }
              }
            }
            
            strength += lineStrength;
          }
        }
      }
      
      angles.push({ angle, strength });
    }
    
    return angles;
  }

  private findDominantAngle(lines: Array<{angle: number, strength: number}>): number {
    if (lines.length === 0) return 0;
    
    // 找出強度最高的角度
    let maxStrength = 0;
    let dominantAngle = 0;
    
    lines.forEach(line => {
      if (line.strength > maxStrength) {
        maxStrength = line.strength;
        dominantAngle = line.angle;
      }
    });
    
    // 如果檢測強度太低，可能是噪音，返回0
    const avgStrength = lines.reduce((sum, line) => sum + line.strength, 0) / lines.length;
    if (maxStrength < avgStrength * 2) {
      return 0;
    }
    
    return dominantAngle;
  }

  private evaluateQuality(
    brightness: number,
    contrast: number, 
    sharpness: number,
    histogram: number[],
    detectedAngle: number
  ): { quality: 'poor' | 'fair' | 'good' | 'excellent'; issues: string[] } {
    const issues: string[] = [];
    let score = 100;

    // 評估亮度
    if (brightness < 80) {
      issues.push('圖片過暗，文字可能難以識別');
      score -= 25;
    } else if (brightness > 200) {
      issues.push('圖片過亮，可能有過曝問題');
      score -= 20;
    }

    // 評估對比度
    if (contrast < 30) {
      issues.push('對比度不足，文字與背景區別不明顯');
      score -= 30;
    }

    // 評估清晰度
    if (sharpness < 15) {
      issues.push('圖片模糊，需要提升清晰度');
      score -= 25;
    }

    // 評估直方圖分佈
    const nonZeroCount = histogram.filter(val => val > 0).length;
    if (nonZeroCount < 100) {
      issues.push('色調範圍過窄，可能需要調整對比度');
      score -= 15;
    }

    // 評估角度傾斜
    const absAngle = Math.abs(detectedAngle);
    if (absAngle > 15) {
      issues.push(`圖片傾斜角度過大 (${detectedAngle.toFixed(1)}°)，嚴重影響OCR識別`);
      score -= 30;
    } else if (absAngle > 5) {
      issues.push(`圖片有輕微傾斜 (${detectedAngle.toFixed(1)}°)，建議校正`);
      score -= 15;
    } else if (absAngle > 2) {
      issues.push(`檢測到微小傾斜 (${detectedAngle.toFixed(1)}°)`);
      score -= 5;
    }

    let quality: 'poor' | 'fair' | 'good' | 'excellent';
    if (score >= 85) quality = 'excellent';
    else if (score >= 70) quality = 'good';
    else if (score >= 50) quality = 'fair';
    else quality = 'poor';

    return { quality, issues };
  }

  private generateRecommendations(
    brightness: number,
    contrast: number,
    sharpness: number,
    _histogram: number[],
    detectedAngle: number
  ): ImageAdjustments {
    let brightnessDelta = 0;
    let contrastDelta = 0;
    let sharpnessDelta = 0;
    let rotationDelta = 0;
    let saturationDelta = 0;
    let confidence = 90;

    // 亮度調整建議
    const idealBrightness = 140; // 理想亮度值
    if (brightness < 80) {
      brightnessDelta = Math.min(50, idealBrightness - brightness);
    } else if (brightness > 200) {
      brightnessDelta = Math.max(-50, idealBrightness - brightness);
    }

    // 對比度調整建議
    if (contrast < 30) {
      contrastDelta = Math.min(40, 50 - contrast);
    } else if (contrast > 80) {
      contrastDelta = Math.max(-20, 60 - contrast);
    }

    // 清晰度調整建議
    if (sharpness < 15) {
      sharpnessDelta = Math.min(50, 30 - sharpness);
    } else if (sharpness > 40) {
      sharpnessDelta = Math.max(-10, 25 - sharpness);
    }

    // 角度校正建議
    const absAngle = Math.abs(detectedAngle);
    if (absAngle > 1) {
      rotationDelta = -detectedAngle; // 反向校正
    }

    // 飽和度調整（通常降低有助於OCR）
    saturationDelta = -10;

    // 根據調整幅度計算信心度
    const totalAdjustment = Math.abs(brightnessDelta) + Math.abs(contrastDelta) + Math.abs(sharpnessDelta) + Math.abs(rotationDelta);
    if (totalAdjustment > 100) confidence = 50;
    else if (totalAdjustment > 80) confidence = 60;
    else if (totalAdjustment > 50) confidence = 75;
    else if (totalAdjustment > 20) confidence = 85;

    return {
      brightness: brightnessDelta,
      contrast: contrastDelta,
      sharpness: sharpnessDelta,
      rotation: rotationDelta,
      saturation: saturationDelta,
      confidence
    };
  }
}